import { useState } from "react";
import { Network, X } from "lucide-react";
import { useData } from "@/context/DataContext";
import { PageHeader, Card, SectionTitle } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { Loading } from "@/pages/_shared";

const GROUP = {
  entity: { c: "#080d1a", label: "Entity" },
  identity: { c: "#1a5fd0", label: "Identity" },
  credit: { c: "#0ea5e9", label: "Credit" },
  cash: { c: "#10b981", label: "Cash" },
  docs: { c: "#f59e0b", label: "Documents" },
  signal: { c: "#8b5cf6", label: "Signal" },
  opportunity: { c: "#ec4899", label: "Opportunity" },
};

function layout(nodes) {
  const center = { x: 300, y: 210 };
  const others = nodes.filter((n) => n.id !== "entity");
  const pos = { entity: center };
  others.forEach((n, i) => {
    const angle = (i / others.length) * Math.PI * 2 - Math.PI / 2;
    pos[n.id] = { x: center.x + Math.cos(angle) * 165, y: center.y + Math.sin(angle) * 135 };
  });
  return pos;
}

export default function FinancialGraph() {
  const { snapshot } = useData();
  const [sel, setSel] = useState(null);
  if (!snapshot) return <Loading />;
  const fg = snapshot.financial_graph;
  const pos = layout(fg.nodes);
  const labelOf = (id) => fg.nodes.find((n) => n.id === id)?.label || id;
  const connected = sel
    ? fg.edges.filter((e) => e.from === sel || e.to === sel).map((e) => (e.from === sel ? e.to : e.from))
    : [];
  const isLit = (id) => !sel || id === sel || connected.includes(id);
  const edgeLit = (e) => !sel || e.from === sel || e.to === sel;
  const selNode = fg.nodes.find((n) => n.id === sel);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="financial-graph-page">
      <PageHeader eyebrow="MMT Financial Graph" title="MMT Financial Graph"
        subtitle="Every provider, document and signal is normalized into one connected graph — the single source of truth powering your readiness."
        badge={<StatusChip state="LIVE" />} />

      <Card className="overflow-hidden p-5">
        <div className="mb-2 flex items-center justify-between">
          <SectionTitle icon={Network}>Connected Financial Graph</SectionTitle>
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{sel ? "Click empty space to reset" : "Click a node to explore"}</span>
        </div>
        <div className="relative w-full overflow-x-auto rounded-xl bg-[#080d1a]" data-testid="financial-graph-canvas">
          <svg viewBox="0 0 600 420" className="h-[420px] w-full min-w-[600px]" onClick={() => setSel(null)}>
            <defs>
              <pattern id="dots" width="22" height="22" patternUnits="userSpaceOnUse"><circle cx="1" cy="1" r="1" fill="rgba(255,255,255,0.05)" /></pattern>
            </defs>
            <rect width="600" height="420" fill="url(#dots)" />
            {fg.edges.map((e, i) => {
              const a = pos[e.from], b = pos[e.to];
              if (!a || !b) return null;
              const lit = edgeLit(e);
              return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={lit ? "#5a8bff" : "#1a5fd0"} strokeOpacity={lit ? 0.7 : 0.12} strokeWidth={lit ? 2 : 1.5} />;
            })}
            {fg.nodes.map((n) => {
              const p = pos[n.id];
              const g = GROUP[n.group] || GROUP.entity;
              const isEntity = n.id === "entity";
              const r = isEntity ? 34 : 24;
              const lit = isLit(n.id);
              return (
                <g key={n.id} data-testid="graph-node" style={{ cursor: "pointer", opacity: lit ? 1 : 0.28, transition: "opacity .2s" }}
                  onClick={(ev) => { ev.stopPropagation(); setSel(n.id === sel ? null : n.id); }}>
                  <circle cx={p.x} cy={p.y} r={r + 6} fill={g.c} fillOpacity={n.id === sel ? 0.4 : 0.15} />
                  <circle cx={p.x} cy={p.y} r={r} fill={g.c} stroke="#ffffff" strokeOpacity={n.id === sel ? 0.9 : 0.15} strokeWidth={n.id === sel ? 2.5 : 1.5} />
                  <text x={p.x} y={p.y + r + 15} textAnchor="middle" fill="#cbd5e1" fontSize="10" fontFamily="JetBrains Mono">{n.label}</text>
                </g>
              );
            })}
          </svg>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          {Object.values(GROUP).map((g) => (
            <div key={g.label} className="flex items-center gap-1.5 text-[11px] text-slate-500"><span className="h-2.5 w-2.5 rounded-full" style={{ background: g.c }} />{g.label}</div>
          ))}
        </div>
      </Card>

      {selNode && (
        <Card className="p-5 animate-fade-up" data-testid="graph-detail">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2.5">
              <span className="h-3 w-3 rounded-full" style={{ background: (GROUP[selNode.group] || GROUP.entity).c }} />
              <div>
                <div className="font-head text-[15px] font-bold text-slate-900">{selNode.label}</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{(GROUP[selNode.group] || GROUP.entity).label} node · {connected.length} connection{connected.length !== 1 ? "s" : ""}</div>
              </div>
            </div>
            <button onClick={() => setSel(null)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {connected.map((id) => (
              <button key={id} onClick={() => setSel(id)} data-testid="graph-connection"
                className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-[12px] font-medium text-slate-700 transition-colors hover:border-[#1a5fd0]/40 hover:text-[#1a5fd0]">
                {labelOf(id)}
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
